import React, { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, Users, Tag } from "lucide-react";

export const BASE_AUDIENCES = [
  { value: "patients", label: "Patients with marketing consent" },
  { value: "subscribers", label: "Website subscribers" },
  { value: "resource_downloaders", label: "Resource downloaders" },
];

export const audienceLabel = (v) => v.startsWith("tag:") ? v.slice(4) : (BASE_AUDIENCES.find(a => a.value === v)?.label || v);

export const uploadImage = async (file, token) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/staff/media/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || "Upload failed");
  return d.url;
};

export const ImageUploadButton = ({ token, onUploaded, label = "Upload image", testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onUploaded(await uploadImage(f, token)); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" size="sm" className="rounded-lg text-xs" disabled={busy} onClick={() => ref.current?.click()} data-testid={testid}>
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}{label}
      </Button>
    </>
  );
};

export const HeaderImageField = ({ value, onChange, token, testid = "header-image" }) => (
  <div className="space-y-2">
    <label className="text-xs font-semibold text-stone-600 block">Header image (optional)</label>
    {value ? (
      <div className="relative rounded-lg overflow-hidden border border-stone-200 max-w-md" data-testid={`${testid}-preview`}>
        <img src={value} alt="Header" className="w-full max-h-40 object-cover" />
        <button onClick={() => onChange("")} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 hover:bg-white" data-testid={`${testid}-remove`}><X className="w-3.5 h-3.5" /></button>
      </div>
    ) : <ImageUploadButton token={token} onUploaded={onChange} label="Upload header image" testid={`${testid}-upload`} />}
  </div>
);

export const AudiencePicker = ({ value, onChange, token }) => {
  const [tags, setTags] = useState([]);
  const [count, setCount] = useState(null);
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  useEffect(() => { fetch(`${API}/staff/audience-tags`, { headers: h }).then(r => r.json()).then(d => setTags(d.tags || [])).catch(() => {}); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!value.length) { setCount(0); return; }
    fetch(`${API}/staff/audience-count`, { method: "POST", headers: h, body: JSON.stringify({ segments: value }) }).then(r => r.json()).then(d => setCount(d.estimated_recipients)).catch(() => {});
  }, [value.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const chip = (v, label, extra, icon) => (
    <button key={v} type="button" onClick={() => toggle(v)} data-testid={`audience-${v}`}
      className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 transition-colors ${value.includes(v) ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-white text-stone-700 border-stone-200 hover:border-[#FF7A00]/50"}`}>
      {icon}{label}{extra}
    </button>
  );
  return (
    <div className="space-y-2" data-testid="audience-picker">
      <label className="text-xs font-semibold text-stone-600 block">Audiences (choose one or more)</label>
      <div className="flex flex-wrap gap-2">{BASE_AUDIENCES.map(a => chip(a.value, a.label, null, <Users className="w-3 h-3" />))}</div>
      {tags.length > 0 && <div className="flex flex-wrap gap-2">{tags.map(t => chip(`tag:${t.tag}`, t.tag, <span className="opacity-70">({t.count})</span>, <Tag className="w-3 h-3" />))}</div>}
      <p className="text-[11px] text-stone-500" data-testid="audience-count">{value.length ? `${count ?? "…"} unique recipients. People in more than one audience get the email once.` : "Select at least one audience."}</p>
    </div>
  );
};
